import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FirebaseError } from 'firebase/app';
import { doc, getDoc, setDoc, updateDoc, DocumentSnapshot, DocumentData, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../hooks/AuthContext';
// import { useAnalytics } from '../../hooks/useAnalytics';
import DOMPurify from 'dompurify';
import { 
  ArrowLeft, 
  Play, 
  CheckCircle,
  Clock,
  BookOpen,
  ClipboardList,
  Award,
  ChevronRight,
  ChevronLeft,
  Target,
  AlertCircle,
  Music,
  Image,
  Home
} from 'lucide-react';
import { Module, Section, Quiz } from '../../types';
import { QuizPlayer } from '../../components/user/QuizPlayer';
import { checkAchievements } from '../../components/user/AchievementSystem';
import { updateStreak } from '../../utils/streakTracker';

interface UserProgress {
  moduleId: string;
  userId: string;
  currentSectionIndex: number;
  completedSections: string[];
  startedAt: Date;
  lastAccessedAt: Date;
  completedAt?: Date;
  timeSpentMinutes: number;
  quizScores: Record<string, number>;
}

export const ModulePlayer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  // const { addEvent } = useAnalytics();

  console.log('🚀 ModulePlayer component rendered', { 
    id, 
    hasUser: !!user, 
    userId: user?.uid,
    authLoading,
    timestamp: new Date().toISOString()
  });

  const [module, setModule] = useState<Module | null>(null);
  const [sections, setSections] = useState<Section[]>([]);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [userProgress, setUserProgress] = useState<UserProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [sectionStartTime, setSectionStartTime] = useState<Date>(new Date());
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [currentQuiz, setCurrentQuiz] = useState<Quiz | null>(null);
  const [quizLoading, setQuizLoading] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  
  // StrictMode guard to prevent double fetch in development
  const didInitRef = useRef(false);

  // Helper function - must be defined before use
  const getSectionIcon = (type: Section['type']) => {
    switch (type) {
      case 'lesson': return BookOpen;
      case 'exercise': return ClipboardList;
      case 'quiz': return Target;
      default: return BookOpen;
    }
  };

  // Helper to get back navigation path based on module type
  const getBackPath = () => {
    return module?.moduleType === 'meditation' ? '/meditations' : '/training';
  };

  // Helper to get back navigation label
  const getBackLabel = () => {
    return module?.moduleType === 'meditation' ? 'Meditations' : 'Training Hub';
  };

  // Load quiz when current section is a quiz
  const loadQuiz = async (quizRef: string) => {
    if (!quizRef) return;
    
    setQuizLoading(true);
    try {
      const quizDoc = await getDoc(doc(db, 'quizzes', quizRef));
      if (quizDoc.exists()) {
        setCurrentQuiz(quizDoc.data() as Quiz);
        console.log('✅ Quiz loaded successfully:', quizDoc.data());
      } else {
        console.error('❌ Quiz not found:', quizRef);
        setCurrentQuiz(null);
      }
    } catch (error) {
      console.error('❌ Error loading quiz:', error);
      setCurrentQuiz(null);
    } finally {
      setQuizLoading(false);
    }
  };

  // Derived values - calculated directly (no useMemo needed for cheap calculations)
  const safeSectionIndexValue = Number.isFinite(currentSectionIndex) ? currentSectionIndex : 0;
  const normalizedSectionIndex = sections.length > 0
    ? Math.min(Math.max(safeSectionIndexValue, 0), sections.length - 1)
    : 0;
  const currentSection = sections.length > 0 ? sections[normalizedSectionIndex] : undefined;
  const isLastSection = normalizedSectionIndex === sections.length - 1;
  const safeSectionId = currentSection?.id ?? `idx_${normalizedSectionIndex}`;
  const isSectionCompleted = !!userProgress?.completedSections.includes(safeSectionId);
  const moduleProgress = sections.length === 0 ? 0 : ((userProgress?.completedSections.length || 0) / sections.length) * 100;
  const SectionIcon = getSectionIcon(currentSection?.type ?? 'lesson');

  // Debug progress calculation
  console.log('📊 Progress Debug:', {
    sectionsLength: sections.length,
    completedSections: userProgress?.completedSections.length || 0,
    completedSectionsList: userProgress?.completedSections,
    moduleProgress: Math.round(moduleProgress),
    currentSectionId: currentSection?.id,
    isSectionCompleted
  });

  // Test if component is mounting
  useEffect(() => {
    console.log('🎯 ModulePlayer component mounted');
    return () => {
      console.log('🎯 ModulePlayer component unmounting');
    };
  }, []);

  useEffect(() => {
    console.log('🔄 ModulePlayer useEffect triggered', { 
      id, 
      hasUser: !!user, 
      userId: user?.uid,
      userRole: user?.role,
      authLoading,
      url: window.location.href,
      pathname: window.location.pathname,
      isFetching
    });
    
    // Wait for auth to resolve before doing anything
    if (authLoading) {
      console.log('⏳ Auth still loading, waiting...');
      return;
    }

    // Handle missing route parameter
    if (!id) {
      console.log('❌ Missing module ID');
      setLoading(false);
      navigate(getBackPath());
      return;
    }

    // Handle unauthenticated user (definitely signed out)
    if (!user) {
      console.log('❌ User not authenticated');
      setLoading(false);
      navigate('/login');
      return;
    }

    // StrictMode guard - prevent double fetch in development
    if (didInitRef.current) {
      console.log('⏸️ Already initialized, skipping duplicate fetch');
      return;
    }
    didInitRef.current = true;

    // Prevent multiple simultaneous fetches
    if (isFetching) {
      console.log('⏸️ Already fetching, skipping');
      return;
    }

    let alive = true;
    setIsFetching(true);

    // Add timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      console.error('⏰ Fetch timeout - forcing loading to false');
      setLoading(false);
      setIsFetching(false);
    }, 10000); // 10 second timeout

    const fetchModuleData = async () => {
      try {
        setLoading(true);
        console.log('🔍 Fetching module data for ID:', id);

        // Fetch module with retry logic
        let moduleDoc: DocumentSnapshot<DocumentData> | null = null;
        let retryCount = 0;
        const maxRetries = 3;
        
        while (retryCount < maxRetries) {
          try {
            console.log(`🔍 Attempting to fetch module document (attempt ${retryCount + 1}):`, id);
            moduleDoc = await getDoc(doc(db, 'modules', id));
            break; // Success, exit retry loop
          } catch (fetchError) {
            retryCount++;
            console.error(`❌ Fetch attempt ${retryCount} failed:`, fetchError);
            
            if (retryCount < maxRetries) {
              console.log(`⏳ Retrying in 1 second... (${retryCount}/${maxRetries})`);
              await new Promise(resolve => setTimeout(resolve, 1000));
            } else {
              throw fetchError; // Re-throw if all retries failed
            }
          }
        }
        
        if (!moduleDoc) {
          throw new Error('Module document could not be loaded');
        }

        console.log('📄 Module document exists:', moduleDoc.exists());
        
        if (!moduleDoc.exists()) {
          console.log('❌ Module not found:', id);
          if (alive) {
            setNotification({ 
              type: 'error', 
              message: `Module not found: ${id}` 
            });
            setLoading(false);
            setIsFetching(false);
          }
          return;
        }

        const moduleData = {
          id: moduleDoc.id,
          ...moduleDoc.data(),
          createdAt: moduleDoc.data().createdAt?.toDate(),
          updatedAt: moduleDoc.data().updatedAt?.toDate(),
          publishedAt: moduleDoc.data().publishedAt?.toDate(),
        } as Module;

        console.log('📦 Module data:', { 
          id: moduleData.id, 
          title: moduleData.title, 
          published: moduleData.published,
          sectionsCount: moduleData.sections?.length || 0,
          sections: moduleData.sections
        });

        if (!moduleData.published) {
          console.log('❌ Module not published:', moduleData.title);
          if (alive) {
            setNotification({ 
              type: 'error', 
              message: 'This module is not yet published.' 
            });
            setLoading(false);
            setIsFetching(false);
          }
          return;
        }

        console.log('🔧 Setting module data, alive flag:', alive);
        if (!alive) {
          console.log('⚠️ Component unmounted, but setting module data anyway');
        }
        setModule(moduleData);

        // Fetch sections from the module document (stored as array field)
        // Fix: Handle undefined order values
        console.log('🔍 Raw moduleData.sections:', moduleData.sections);
        const sectionsData = (moduleData.sections || [])
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) as Section[];
        
        console.log('📚 Sections data:', sectionsData.map(s => ({ id: s.id, title: s.title, type: s.type })));
        console.log('📚 Sections count:', sectionsData.length);
        
        console.log('🔧 Setting sections data, alive flag:', alive);
        if (!alive) {
          console.log('⚠️ Component unmounted, but setting sections data anyway');
        }
        setSections(sectionsData);

        // Fetch or create user progress
        console.log('👤 Fetching user progress for:', `${user.uid}_${id}`);
        const progressDoc = await getDoc(doc(db, 'userProgress', `${user.uid}_${id}`));
        if (progressDoc.exists()) {
          const progressData = {
            ...progressDoc.data(),
            startedAt: progressDoc.data().startedAt?.toDate(),
            lastAccessedAt: progressDoc.data().lastAccessedAt?.toDate(),
            completedAt: progressDoc.data().completedAt?.toDate(),
          } as UserProgress;
          console.log('📊 Existing progress:', progressData);
          
          const rawSavedIndex = typeof progressData.currentSectionIndex === 'number' && Number.isFinite(progressData.currentSectionIndex)
            ? progressData.currentSectionIndex
            : 0;
          const clampedIndex = sectionsData.length > 0
            ? Math.min(Math.max(rawSavedIndex, 0), sectionsData.length - 1)
            : 0;

          console.log('🔧 Setting existing progress, alive flag:', alive, {
            originalIndex: progressData.currentSectionIndex,
            clampedIndex,
            sectionsLength: sectionsData.length
          });
          if (!alive) {
            console.log('⚠️ Component unmounted, but setting progress data anyway');
          }
          const normalizedProgress: UserProgress = {
            ...progressData,
            currentSectionIndex: clampedIndex
          };
          setUserProgress(normalizedProgress);
          setCurrentSectionIndex(clampedIndex);
          await syncSummaryProgress(normalizedProgress, sectionsData.length);
        } else {
          // Create new progress
          console.log('🆕 Creating new progress');
          const newProgress: UserProgress = {
            moduleId: id,
            userId: user.uid,
            currentSectionIndex: 0,
            completedSections: [],
            startedAt: new Date(),
            lastAccessedAt: new Date(),
            timeSpentMinutes: 0,
            quizScores: {}
          };
          await setDoc(doc(db, 'userProgress', `${user.uid}_${id}`), newProgress);
          
          console.log('🔧 Setting new progress, alive flag:', alive);
          if (!alive) {
            console.log('⚠️ Component unmounted, but setting new progress data anyway');
          }
          setUserProgress(newProgress);

          await syncSummaryProgress(newProgress, sectionsData.length);
        }

        // Track module view (moved outside useEffect to prevent infinite loops)
        // await addEvent('module.view', { moduleId: id });

        // Success! Set loading to false
        console.log('🎯 Success path reached, alive flag:', alive);
        console.log('✅ Module data loaded successfully');
        setLoading(false);
        setIsFetching(false);
        
        if (!alive) {
          console.log('⚠️ Component unmounted, but loading state was set');
        }

      } catch (error: unknown) {
        console.error('❌ Error fetching module data:', error);

        const firebaseError = error instanceof FirebaseError ? error : null;
        const genericError = error instanceof Error ? error : null;

        console.error('Error details:', {
          message: genericError?.message ?? 'Unknown error',
          stack: genericError?.stack,
          id,
          userId: user?.uid,
          errorCode: firebaseError?.code,
          errorName: firebaseError?.name
        });
        
        // Check for specific Firestore errors
        if (firebaseError?.code === 'failed-precondition') {
          console.error('🚨 Firestore composite index required! Check Firebase console for index creation link.');
          setNotification({ 
            type: 'error', 
            message: 'Database index required. Please contact support or try again later.' 
          });
        } else if (firebaseError?.code === 'permission-denied') {
          console.error('🚨 Firestore permission denied! Check security rules.');
          setNotification({ 
            type: 'error', 
            message: 'Permission denied. Please check your access rights.' 
          });
        } else if (firebaseError?.code === 'unavailable' || firebaseError?.message?.includes('400')) {
          console.error('🚨 Firestore connection unavailable! Network or service issue.');
          setNotification({ 
            type: 'error', 
            message: 'Connection issue. Please check your internet connection and try again.' 
          });
        } else if (genericError?.message?.includes('disconnected port')) {
          console.error('🚨 Browser extension or proxy issue detected.');
          setNotification({ 
            type: 'error', 
            message: 'Browser extension conflict detected. Please disable extensions and try again.' 
          });
        } else {
          console.error('🚨 Unknown error:', error);
          setNotification({ 
            type: 'error', 
            message: 'Failed to load module. Please try again.' 
          });
        }
        
        if (alive) {
          setLoading(false);
          setIsFetching(false);
        }
        // Don't auto-navigate on error - let user see the error message
        return;
      } finally {
        console.log('🧹 Finally block reached, alive flag:', alive);
        console.log('✅ Finally block: clearing timeout and setting loading false');
        clearTimeout(timeoutId);
        setLoading(false);
        setIsFetching(false);
        
        if (!alive) {
          console.log('⚠️ Finally block: component unmounted, but cleanup completed');
        }
      }
    };

    fetchModuleData();
    
    return () => { 
      alive = false; 
      clearTimeout(timeoutId);
      // No state setters in cleanup - rely on alive flag
    };
  }, [id, user, authLoading, navigate, retryToken, isFetching]);

  // Clamp index to prevent out of bounds
  useEffect(() => {
    if (sections.length === 0) return;

    if (currentSectionIndex >= sections.length) {
      setCurrentSectionIndex(sections.length - 1);
      return;
    }

    if (currentSectionIndex < 0) {
      setCurrentSectionIndex(0);
    }
  }, [sections.length, currentSectionIndex]);

  // Reset timer when sections are loaded
  useEffect(() => {
    if (sections.length > 0) {
      setSectionStartTime(new Date());
    }
  }, [sections.length]);

  // Load quiz when current section is a quiz
  useEffect(() => {
    if (currentSection?.type === 'quiz' && currentSection.quizRef) {
      console.log('🎯 Loading quiz for section:', currentSection.quizRef);
      loadQuiz(currentSection.quizRef);
    } else {
      setCurrentQuiz(null);
    }
  }, [currentSection]);

  // Handle quiz completion
  const handleQuizComplete = async (score: number, timeSpent: number) => {
    if (!user || !id || !userProgress || !currentQuiz || !currentSection) return;

    try {
      console.log('🎯 Starting quiz completion process:', {
        score,
        timeSpent,
        currentQuizId: currentQuiz.id,
        currentSectionId: currentSection.id,
        userId: user.uid
      });

      // Update quiz scores
      const updatedScores = {
        ...userProgress.quizScores,
        [currentQuiz.id]: score
      };

      // Mark section as completed
      const updatedCompletedSections = [...userProgress.completedSections];
      if (!updatedCompletedSections.includes(currentSection.id)) {
        updatedCompletedSections.push(currentSection.id);
        console.log('✅ Added section to completed list:', currentSection.id);
      }

      // Update progress
      await updateProgress({
        quizScores: updatedScores,
        completedSections: updatedCompletedSections
      });

      setNotification({ 
        type: 'success', 
        message: `Quiz completed! Score: ${score}%` 
      });

      console.log('✅ Quiz completed successfully:', { 
        score, 
        timeSpent, 
        completedSections: updatedCompletedSections.length,
        totalSections: sections.length
      });

      // Check for achievements
      if (user) {
        await checkAchievements(user, false, score, timeSpent);
      }
    } catch (error) {
      console.error('❌ Error completing quiz:', error);
      setNotification({ 
        type: 'error', 
        message: 'Failed to save quiz results' 
      });
    }
  };

  const syncSummaryProgress = useCallback(async (nextProgress: UserProgress, sectionsOverride?: number) => {
    if (!user || !id) return;

    try {
      const totalSections = sectionsOverride ?? sections.length;
      const completedCount = nextProgress.completedSections.length;
      const rawPercent = totalSections > 0
        ? Math.round((completedCount / totalSections) * 100)
        : 0;
      const percent = Math.min(100, Math.max(0, rawPercent));

      const summaryRef = doc(db, 'userProgress', user.uid);
      const payload: Record<string, unknown> = {
        uid: user.uid,
        updatedAt: new Date(),
        moduleProgress: {
          [id]: percent
        }
      };

      if (nextProgress.completedAt || percent >= 100) {
        payload.completedModules = arrayUnion(id);
        payload.lastCompletedAt = new Date();
      } else {
        payload.completedModules = arrayRemove(id);
      }

      await setDoc(summaryRef, payload, { merge: true });
    } catch (error) {
      console.error('Error syncing user training summary:', error);
    }
  }, [user, id, sections.length]);

  const updateProgress = async (updates: Partial<UserProgress>) => {
    if (!user || !id || !userProgress) return;

    try {
      const sanitizedUpdates: Partial<UserProgress> = { ...updates };
      if (typeof sanitizedUpdates.currentSectionIndex === 'number') {
        const safeIndex = Number.isFinite(sanitizedUpdates.currentSectionIndex)
          ? sanitizedUpdates.currentSectionIndex
          : 0;

        if (sections.length > 0) {
          sanitizedUpdates.currentSectionIndex = Math.min(
            Math.max(safeIndex, 0),
            sections.length - 1
          );
        } else {
          sanitizedUpdates.currentSectionIndex = 0;
        }
      }

      const nextCurrentSectionIndex = typeof sanitizedUpdates.currentSectionIndex === 'number'
        ? sanitizedUpdates.currentSectionIndex
        : userProgress.currentSectionIndex;

      const nextCompletedSections = sanitizedUpdates.completedSections
        ? [...sanitizedUpdates.completedSections]
        : [...userProgress.completedSections];

      const nextQuizScores = sanitizedUpdates.quizScores
        ? { ...sanitizedUpdates.quizScores }
        : { ...userProgress.quizScores };

      const nextTimeSpent = sanitizedUpdates.timeSpentMinutes ?? userProgress.timeSpentMinutes;
      const nextCompletedAt = sanitizedUpdates.completedAt ?? userProgress.completedAt;
      const nextLastAccessedAt = new Date();

      const firestoreUpdate: Record<string, unknown> = {
        lastAccessedAt: nextLastAccessedAt,
        currentSectionIndex: nextCurrentSectionIndex,
        timeSpentMinutes: nextTimeSpent,
        completedSections: nextCompletedSections,
        quizScores: nextQuizScores,
      };

      if (sanitizedUpdates.completedAt !== undefined) {
        firestoreUpdate.completedAt = nextCompletedAt ?? null;
      }

      await updateDoc(doc(db, 'userProgress', `${user.uid}_${id}`), firestoreUpdate);

      const nextProgressState: UserProgress = {
        ...userProgress,
        currentSectionIndex: nextCurrentSectionIndex,
        completedSections: nextCompletedSections,
        timeSpentMinutes: nextTimeSpent,
        quizScores: nextQuizScores,
        completedAt: nextCompletedAt,
        lastAccessedAt: nextLastAccessedAt
      };

      setUserProgress(nextProgressState);

      await syncSummaryProgress(nextProgressState);
    } catch (error) {
      console.error('Error updating progress:', error);
      // Show user-friendly error message
      setNotification({ type: 'error', message: 'Failed to save progress. Your progress will be saved locally.' });
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const markSectionComplete = async () => {
    if (!userProgress || sections.length === 0) return;

    const activeIndex = Math.min(Math.max(currentSectionIndex, 0), sections.length - 1);
    const currentSection = sections[activeIndex];
    const timeSpent = Math.round((new Date().getTime() - sectionStartTime.getTime()) / 60000);
    
    const completedSections = [...userProgress.completedSections];
    const safeSectionId = currentSection?.id ?? `idx_${activeIndex}`;
    if (!completedSections.includes(safeSectionId)) {
      completedSections.push(safeSectionId);
    }

    await updateProgress({
      completedSections,
      timeSpentMinutes: userProgress.timeSpentMinutes + timeSpent,
      currentSectionIndex: Math.min(activeIndex + 1, sections.length - 1)
    });

    // Track section completion (analytics disabled)
    // await addEvent('module.complete', { 
    //   moduleId: id, 
    //   sectionId: currentSection.id,
    //   durationMs: timeSpent * 60000
    // });
  };

  const goToNextSection = async () => {
    if (currentSectionIndex < sections.length - 1) {
      const next = currentSectionIndex + 1;
      setCurrentSectionIndex(next);
      setSectionStartTime(new Date());
      await updateProgress({ currentSectionIndex: next });
    }
  };

  const goToPreviousSection = async () => {
    if (currentSectionIndex > 0) {
      const prev = currentSectionIndex - 1;
      setCurrentSectionIndex(prev);
      setSectionStartTime(new Date());
      await updateProgress({ currentSectionIndex: prev });
    }
  };

  const completeModule = async () => {
    if (!userProgress) return;

    const finalIndex = sections.length > 0 ? sections.length - 1 : 0;

    await updateProgress({
      completedAt: new Date(),
      currentSectionIndex: finalIndex
    });

    // Update streak and check for achievements
    if (user) {
      await updateStreak(user.uid, 'module');
      await checkAchievements(user, true, undefined, userProgress.timeSpentMinutes);
    }

    // Track module completion (analytics disabled)
    // await addEvent('module.complete', { moduleId: id });

    // Show completion celebration
    navigate(getBackPath(), { state: { moduleCompleted: module?.title } });
  };

  const getSectionColor = (type: Section['type']) => {
    switch (type) {
      case 'lesson': return 'from-blue-500 to-indigo-500';
      case 'exercise': return 'from-green-500 to-emerald-500';
      case 'quiz': return 'from-purple-500 to-pink-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="bg-gradient-to-br from-blue-500 to-purple-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto shadow-2xl mb-6 animate-pulse">
            <Play className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Loading Your Training Module</h2>
          <p className="text-gray-600 text-base">Preparing your personalized coaching experience...</p>
          <div className="mt-6 flex justify-center">
            <div className="flex space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Debug logging
  console.log('🔍 ModulePlayer render state:', {
    loading,
    hasModule: !!module,
    moduleTitle: module?.title,
    sectionsCount: sections.length,
    sections: sections.map(s => ({ id: s.id, title: s.title, type: s.type }))
  });


  if (!module) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex flex-col items-center justify-center px-4 text-center">
        <div className="max-w-sm w-full space-y-4">
          <div className="mx-auto w-20 h-20 rounded-2xl bg-gradient-to-br from-gray-300 to-gray-200 flex items-center justify-center shadow-lg">
            <AlertCircle className="w-10 h-10 text-gray-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">We couldn't load that module</h2>
          <p className="text-gray-600 text-sm">
            It may have been unpublished, moved, or there was a connection hiccup. Head back to the Training Hub and try again.
          </p>
          <div className="space-y-2">
            <button
              onClick={() => {
                console.log('🔁 Retrying module load from fallback state');
                didInitRef.current = false;
                setIsFetching(false);
                setLoading(true);
                setRetryToken(prev => prev + 1);
              }}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition-colors"
            >
              Retry loading module
            </button>
            <button
              onClick={() => navigate(getBackPath())}
              className="w-full px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
            >
              Back to {getBackLabel()}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Handle case where module exists but has no sections
  if (sections.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Module Has No Content</h2>
          <p className="text-gray-600 mb-4">This module doesn't have any sections yet.</p>
          <button
            onClick={() => navigate(getBackPath())}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            Back to {getBackLabel()}
          </button>
        </div>
      </div>
    );
  }

  if (!currentSection) {
    console.warn('🚧 ModulePlayer missing current section after normalization', {
      moduleId: module?.id ?? id,
      normalizedSectionIndex,
      sectionsCount: sections.length,
      userProgressSectionIndex: userProgress?.currentSectionIndex
    });

    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex flex-col items-center justify-center px-4 text-center">
        <div className="max-w-sm w-full space-y-4">
          <div className="mx-auto w-20 h-20 rounded-2xl bg-gradient-to-br from-yellow-200 to-yellow-100 flex items-center justify-center shadow-lg">
            <AlertCircle className="w-10 h-10 text-yellow-700" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">We couldn’t find this section</h2>
          <p className="text-gray-600 text-sm">
            Your progress points to a section that no longer exists. We’ve reset you to the start—try reopening the module.
          </p>
          <div className="space-y-2">
            <button
              onClick={() => {
                didInitRef.current = false;
                setIsFetching(false);
                setLoading(true);
                setRetryToken(prev => prev + 1);
              }}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition-colors"
            >
              Reload module
            </button>
            <button
              onClick={() => navigate(getBackPath())}
              className="w-full px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
            >
              Back to {getBackLabel()}
            </button>
          </div>
        </div>
      </div>
    );
  }


  console.log('🎬 ModulePlayer about to render', {
    loading,
    hasModule: !!module,
    sectionsCount: sections.length,
    currentSectionIndex,
    hasUserProgress: !!userProgress
  });

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 overflow-x-hidden"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
    >
      {/* Notification Toast */}
      {notification && (
        <div className={`fixed inset-x-4 sm:right-4 sm:left-auto top-4 z-50 p-4 rounded-lg shadow-lg transition-all duration-300 max-w-md mx-auto sm:mx-0 ${
          notification.type === 'error' 
            ? 'bg-red-500 text-white' 
            : 'bg-green-500 text-white'
        }`}>
          <div className="flex items-center space-x-2">
            <span>{notification.message}</span>
            {notification.type === 'error' && (
              <button 
                onClick={() => {
                  setNotification(null);
                  // Reset the fetch state and retry
                  setIsFetching(false);
                  didInitRef.current = false;
                  setLoading(true);
                  setRetryToken(prev => prev + 1);
                }}
                className="ml-2 px-2 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 rounded text-sm"
              >
                Retry
              </button>
            )}
            <button 
              onClick={() => setNotification(null)}
              className="ml-2 text-white hover:text-gray-200"
            >
              ×
            </button>
          </div>
        </div>
      )}
      
      {/* Mobile-Optimized Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="w-full px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate(module?.moduleType === 'meditation' ? '/meditations' : '/training')}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-all duration-200"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="min-w-0 flex-1">
                <h1 className="text-lg md:text-xl font-bold text-gray-900 truncate">{module?.title ?? 'Training Module'}</h1>
                <p className="text-xs md:text-sm text-gray-600 truncate">
                  {normalizedSectionIndex + 1}/{sections.length} • {currentSection.title}
                </p>
              </div>
            </div>

            {/* Mobile Time Spent */}
            <div className="flex items-center space-x-1 text-xs md:text-sm text-gray-600 flex-shrink-0">
              <Clock className="w-3 h-3 md:w-4 md:h-4" />
              <span>{userProgress?.timeSpentMinutes || 0}m</span>
            </div>
          </div>

          {/* Mobile Progress */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs text-gray-600">
              <span>Progress: {Math.round(moduleProgress)}%</span>
              <span>{userProgress?.completedSections.length || 0}/{sections.length} sections</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${moduleProgress}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile-Optimized Content */}
      <div className="w-full px-4 pt-4 sm:pt-6 pb-[calc(env(safe-area-inset-bottom,0px)+96px)]">
        <div className="w-full max-w-full space-y-4 sm:space-y-6">
          {/* Section Header - Mobile Optimized */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 w-full space-y-4">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className={`bg-gradient-to-br ${getSectionColor(currentSection.type)} p-2 sm:p-3 rounded-xl shadow-lg flex-shrink-0`}>
                <SectionIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">{currentSection.title}</h2>
                <div className="flex items-center space-x-2 mt-1">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {currentSection.type.charAt(0).toUpperCase() + currentSection.type.slice(1)}
                  </span>
                  <span className="text-xs text-gray-600">
                    {normalizedSectionIndex + 1}/{sections.length}
                  </span>
                </div>
              </div>
            </div>

            {/* Audio Player - Mobile Optimized */}
            {currentSection.media?.type === 'audio' && (
              <div className="space-y-3">
                <div className="bg-gray-900 rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
                  <div className="p-4 flex flex-col gap-3">
                    <div className="flex items-center space-x-3">
                      <div className="bg-purple-100 p-2 rounded-lg">
                        <Music className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="min-w-0 text-white">
                        <h3 className="font-semibold text-sm sm:text-base">{currentSection.title}</h3>
                        <p className="text-gray-300 text-xs">Audio coaching session</p>
                      </div>
                    </div>
                    <audio
                      controls
                      className="w-full"
                      onEnded={() => {
                        if (!isSectionCompleted) {
                          markSectionComplete();
                        }
                      }}
                    >
                      <source src={currentSection.media.downloadURL} type={currentSection.media.mimeType || 'audio/mpeg'} />
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                  <div className="bg-white/10 px-4 py-2 text-xs sm:text-sm text-gray-200">
                    Make sure to listen to the full recording—your progress updates automatically when complete.
                  </div>
                </div>
                {!isSectionCompleted && (
                  <div className="flex justify-end text-xs text-purple-600 font-medium">
                    Listen to continue →
                  </div>
                )}
              </div>
            )}

            {/* Video Player - Mobile Optimized */}
            {currentSection.media?.type === 'video' && (
              <div className="space-y-3">
                <div className="aspect-video bg-black rounded-2xl overflow-hidden shadow-xl">
                  <video
                    src={currentSection.media.downloadURL}
                    poster={currentSection.media.thumbnail || undefined}
                    controls
                    className="w-full h-full"
                    onEnded={() => {
                      if (!isSectionCompleted) {
                        markSectionComplete();
                      }
                    }}
                  />
                </div>
                {!isSectionCompleted && (
                  <div className="flex justify-end text-xs text-blue-600 font-medium">
                    Watch to continue →
                  </div>
                )}
              </div>
            )}

            {/* Image Display - Mobile Optimized */}
            {currentSection.media?.type === 'image' && (
              <div className="space-y-3">
                <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200">
                  <div className="p-3 sm:p-4">
                    <div className="flex items-center space-x-3 sm:space-x-4 mb-3">
                      <div className="bg-blue-500 p-2 rounded-lg">
                        <Image className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-gray-900 font-semibold text-sm sm:text-base">{currentSection.title}</h3>
                        <p className="text-gray-600 text-xs">View the image below</p>
                      </div>
                    </div>
                    
                    <div className="relative">
                      <img
                        src={currentSection.media.downloadURL}
                        alt={currentSection.title}
                        className="w-full h-auto max-w-full rounded-lg md:rounded-xl shadow-lg"
                        onLoad={() => {
                          // Mark section as completed when image loads
                          if (!isSectionCompleted) {
                            markSectionComplete();
                          }
                        }}
                        onError={(e) => {
                          console.error('Image failed to load:', e);
                        }}
                      />
                    </div>
                  </div>
                </div>
                {!isSectionCompleted && (
                  <div className="flex justify-end text-xs text-blue-600 font-medium">
                    View to continue →
                  </div>
                )}
              </div>
            )}

            {/* Quiz Content - Mobile Optimized */}
            {currentSection.type === 'quiz' && currentQuiz ? (
              <div className="space-y-3">
                {quizLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-gray-600 text-sm">Loading quiz...</span>
                  </div>
                ) : currentQuiz && currentQuiz.questions && currentQuiz.questions.length > 0 ? (
                  <QuizPlayer
                    quiz={currentQuiz}
                    onComplete={handleQuizComplete}
                    onExit={() => navigate(getBackPath())}
                  />
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 text-center space-y-3">
                    <div className="flex items-center justify-center">
                      <AlertCircle className="w-6 h-6 text-yellow-600" />
                    </div>
                    <h3 className="text-base font-semibold text-yellow-900">Quiz Not Ready</h3>
                    <p className="text-yellow-700 text-sm">
                      This quiz section is not yet configured with questions.
                    </p>
                    <button
                      onClick={() => navigate(getBackPath())}
                      className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors text-sm"
                    >
                      Return to Training
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* Text Content for non-quiz sections - Mobile Optimized */
              currentSection.content && (
                <div className="prose prose-sm sm:prose-base max-w-none">
                  <div 
                    className="text-gray-700 leading-relaxed text-sm"
                    dangerouslySetInnerHTML={{ 
                      __html: DOMPurify.sanitize(
                        currentSection.content
                          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          .replace(/(^|[^*])\*(?!\*)([^*]+)\*(?=[^*]|$)/g, '$1<em>$2</em>')
                          .replace(/^## (.*$)/gim, '<h2 class="text-lg md:text-xl font-semibold mb-3 md:mb-4 text-gray-900">$1</h2>')
                          .replace(/^# (.*$)/gim, '<h1 class="text-xl md:text-2xl font-bold mb-4 md:mb-6 text-gray-900">$1</h1>')
                          .replace(/\n/g, '<br />')
                      )
                    }}
                  />
                </div>
              )
            )}

            {/* Section Actions - Mobile Optimized */}
            {currentSection.type !== 'quiz' && (
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                <button
                  onClick={goToPreviousSection}
                  disabled={normalizedSectionIndex === 0}
                  className="flex items-center justify-center space-x-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Previous</span>
                </button>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                  {!isSectionCompleted && (
                    <button
                      onClick={markSectionComplete}
                      className="flex items-center justify-center space-x-2 px-4 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-all duration-300 shadow-lg hover:shadow-xl text-sm"
                    >
                      <CheckCircle className="w-4 h-4" />
                      <span>Mark Complete</span>
                    </button>
                  )}

                  {isLastSection && isSectionCompleted ? (
                    <button
                      onClick={completeModule}
                      className="flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-300 shadow-lg hover:shadow-xl text-sm"
                    >
                      <Award className="w-4 h-4" />
                      <span>Complete Module</span>
                    </button>
                  ) : (
                    <button
                      onClick={goToNextSection}
                      disabled={normalizedSectionIndex === sections.length - 1}
                      className="flex items-center justify-center space-x-2 px-4 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      <span>Next</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Mobile Section Navigation */}
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 sm:p-6">
            <h3 className="font-bold text-gray-900 mb-3 text-sm md:text-base">Module Sections</h3>
            <div className="space-y-1.5 max-h-64 md:max-h-96 overflow-y-auto custom-scrollbar">
              {sections.map((section, index) => {
                const SectionIcon = getSectionIcon(section.type);
                const isCompleted = userProgress?.completedSections.includes(section.id);
                const isCurrent = index === normalizedSectionIndex;
                
                return (
                  <button
                    key={section.id}
                    onClick={async () => {
                      setCurrentSectionIndex(index);
                      await updateProgress({ currentSectionIndex: index });
                    }}
                    className={`w-full flex items-center space-x-2 md:space-x-3 p-1.5 md:p-3 rounded-lg md:rounded-xl transition-all duration-300 text-left ${
                      isCurrent 
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg'
                        : isCompleted
                        ? 'bg-green-50 text-green-700 hover:bg-green-100'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <div className={`p-1 md:p-2 rounded-md md:rounded-lg flex-shrink-0 ${
                      isCurrent 
                        ? 'bg-white bg-opacity-20'
                        : isCompleted
                        ? 'bg-green-200'
                        : 'bg-gray-200'
                    }`}>
                      {isCompleted ? (
                        <CheckCircle className="w-3 h-3 md:w-4 md:h-4" />
                      ) : (
                        <SectionIcon className="w-3 h-3 md:w-4 md:h-4" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-xs md:text-sm truncate">{section.title}</div>
                      <div className={`text-xs ${isCurrent ? 'text-blue-100' : isCompleted ? 'text-green-600' : 'text-gray-500'}`}>
                        {section.type.charAt(0).toUpperCase() + section.type.slice(1)} • {index + 1}/{sections.length}
                      </div>
                    </div>
                    {isCurrent && (
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-white rounded-full animate-pulse flex-shrink-0"></div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Break */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900">Need a break?</h3>
            <p className="text-gray-600 text-sm">
              Your progress is saved automatically. You can resume this module anytime from the training hub.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <button
              onClick={() => navigate(getBackPath())}
              className="inline-flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{getBackLabel()}</span>
            </button>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center justify-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all text-sm"
            >
              <Home className="w-4 h-4" />
              <span>Dashboard</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
